import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { API_CONFIG } from '../../config/api.js';

const CountryCodeSelector = ({ 
  value, 
  onChange, 
  disabled = false, 
  className = "",
  placeholder = "Select country"
}) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_CONFIG.BASE_URL}/countries`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch countries');
        }
        
        const result = await response.json();
        
        if (result.success) {
          setCountries(result.data);
        } else {
          throw new Error('Invalid response');
        }
        
      } catch (error) {
        console.error('Failed to load countries:', error);
        // Use fallback countries
        setCountries(getFallbackCountries());
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getFallbackCountries = () => [
    { iso: 'IN', name: 'India', phoneCode: '+91', flag: '' },
    { iso: 'US', name: 'United States', phoneCode: '+1', flag: '' },
    { iso: 'GB', name: 'United Kingdom', phoneCode: '+44', flag: '🇧' },
    { iso: 'CA', name: 'Canada', phoneCode: '+1', flag: '🇨🇦' },
    { iso: 'AU', name: 'Australia', phoneCode: '+61', flag: '' },
    { iso: 'DE', name: 'Germany', phoneCode: '+49', flag: '' },
    { iso: 'FR', name: 'France', phoneCode: '+33', flag: '🇷' },
    { iso: 'JP', name: 'Japan', phoneCode: '+81', flag: '🇵' },
    { iso: 'CN', name: 'China', phoneCode: '+86', flag: '' },
    { iso: 'AE', name: 'UAE', phoneCode: '+971', flag: '🇦🇪' },
    { iso: 'SG', name: 'Singapore', phoneCode: '+65', flag: '' },
    { iso: 'BR', name: 'Brazil', phoneCode: '+55', flag: '🇷' },
    { iso: 'IT', name: 'Italy', phoneCode: '+39', flag: '🇹' },
    { iso: 'ES', name: 'Spain', phoneCode: '+34', flag: '🇸' },
    { iso: 'RU', name: 'Russia', phoneCode: '+7', flag: '🇷🇺' },
    { iso: 'KR', name: 'South Korea', phoneCode: '+82', flag: '🇰🇷' },
    { iso: 'NL', name: 'Netherlands', phoneCode: '+31', flag: '🇳🇱' },
    { iso: 'CH', name: 'Switzerland', phoneCode: '+41', flag: '' },
    { iso: 'SE', name: 'Sweden', phoneCode: '+46', flag: '' },
    { iso: 'NO', name: 'Norway', phoneCode: '+47', flag: '' }
  ];

  const selectedCountry = countries.find(country => country.phoneCode === value);
  
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.phoneCode.includes(searchTerm)
  );

  const handleCountrySelect = (country) => {
    // Create synthetic event to match expected format
    const syntheticEvent = {
      target: {
        value: country.phoneCode
      }
    };
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full h-9 sm:h-10 px-2 sm:px-3 border border-gray-300 rounded-md bg-gray-100 text-gray-500 text-xs sm:text-sm flex items-center justify-center">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Country Display (Flag Only) */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 sm:h-10 px-2 sm:px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-xs sm:text-sm flex items-center justify-between ${
          disabled ? 'bg-gray-100 text-gray-500' : 'bg-white text-gray-900'
        }`}
      >
        <span className="text-lg leading-none">
          {selectedCountry ? selectedCountry.flag : '🌍'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-80 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden left-0">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoComplete="off"
              autoFocus
            />
          </div>

          {/* Country List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.iso}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2 ${
                    country.phoneCode === value ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="flex-1">{country.name}</span>
                  <span className="text-gray-500">{country.phoneCode}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelector; 